import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import {
  EQUIPMENT_TYPES,
  EXTINGUISHER_TYPES,
  EQUIPMENT_TYPE_LABELS,
  EQUIPMENT_STORED_STATUS_OPTIONS,
} from '../../utils/constants';
import EquipmentCalculatedStatus from '../../components/EquipmentCalculatedStatus';
import { parseMaintenanceDate } from '../../utils/helpers';

const formatDateInput = (date) => {
  const d = parseMaintenanceDate(date);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addSixMonthsIso = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const d = parseMaintenanceDate(yyyyMmDd);
  if (!d) return '';
  d.setMonth(d.getMonth() + 6);
  return formatDateInput(d);
};

const emptyForm = () => ({
  type: '',
  clientId: '',
  siteId: '',
  planId: '',
  serialNumber: '',
  extinguisherType: '',
  capacity: '',
  inventoryNumber: '',
  detailedType: '',
  manufacturingDate: '',
  lastMaintenanceDate: '',
  nextMaintenanceDate: '',
  expirationDate: '',
  location: '',
  detailedLocation: '',
  vehicleRegistration: '',
  vehicleSite: '',
  vehicleType: '',
  structure: '',
  affectation: '',
  physicalCondition: '',
  spareParts: '',
  firstAidKit: '',
  notes: '',
  observation: '',
  status: 'operational',
  recoveryDate: '',
  recoveredBy: '',
});

const EditEquipment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { id } = useParams();
  const returnTo =
    new URLSearchParams(location.search).get('returnTo') || '/equipment';
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (formData.clientId) {
      loadSites(formData.clientId);
    } else {
      setSites([]);
    }
  }, [formData.clientId]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const [equipmentData, clientsData] = await Promise.all([
        apiService.getEquipmentById(id),
        apiService.getClients(),
      ]);

      setClients(clientsData);

      setFormData({
        ...emptyForm(),
        type: equipmentData.type || '',
        clientId: equipmentData.clientId != null ? String(equipmentData.clientId) : '',
        siteId: equipmentData.siteId != null ? String(equipmentData.siteId) : '',
        planId: equipmentData.planId ?? '',
        serialNumber: equipmentData.serialNumber || '',
        extinguisherType: equipmentData.extinguisherType || '',
        capacity: equipmentData.capacity || '',
        inventoryNumber: equipmentData.inventoryNumber || '',
        detailedType: equipmentData.detailedType || '',
        manufacturingDate: formatDateInput(equipmentData.manufacturingDate),
        lastMaintenanceDate: formatDateInput(equipmentData.lastMaintenanceDate),
        nextMaintenanceDate: formatDateInput(equipmentData.nextMaintenanceDate),
        expirationDate: formatDateInput(equipmentData.expirationDate),
        location: equipmentData.location || '',
        detailedLocation: equipmentData.detailedLocation || '',
        vehicleRegistration: equipmentData.vehicleRegistration || '',
        vehicleSite: equipmentData.vehicleSite || '',
        vehicleType: equipmentData.vehicleType || '',
        structure: equipmentData.structure || '',
        affectation: equipmentData.affectation || '',
        physicalCondition: equipmentData.physicalCondition || '',
        spareParts: equipmentData.spareParts || '',
        firstAidKit: equipmentData.firstAidKit || '',
        notes: equipmentData.notes || '',
        observation: equipmentData.observation || '',
        status: equipmentData.status || 'operational',
        recoveryDate: formatDateInput(equipmentData.recoveryDate),
        recoveredBy: equipmentData.recoveredBy || '',
      });
    } catch (error) {
      console.error('Failed to load equipment:', error);
      setErrors({ load: 'Impossible de charger les données de l\'équipement' });
    } finally {
      setLoadingData(false);
    }
  };

  const loadSites = async (clientId) => {
    try {
      const data = await apiService.getSitesByClient(clientId);
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'lastMaintenanceDate' && value) {
      setFormData({
        ...formData,
        [name]: value,
        nextMaintenanceDate: addSixMonthsIso(value),
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const isExtinguisher =
    formData.type === EQUIPMENT_TYPES.EXTINGUISHER ||
    (!!formData.extinguisherType &&
      formData.type !== EQUIPMENT_TYPES.RIA &&
      formData.type !== EQUIPMENT_TYPES.BDI &&
      formData.type !== EQUIPMENT_TYPES.CABINET);

  const validate = () => {
    const newErrors = {};

    if (!isExtinguisher && !formData.type) {
      newErrors.type = "Le type d'équipement est requis";
    }
    if (!formData.clientId) newErrors.clientId = 'Le client est requis';
    if (!formData.siteId) newErrors.siteId = 'Le site est requis';
    if (!formData.serialNumber) newErrors.serialNumber = 'Le numéro de série est requis';

    if (isExtinguisher) {
      if (!formData.extinguisherType) newErrors.extinguisherType = "Le type d'extincteur est requis";
      if (!formData.capacity) newErrors.capacity = 'La capacité est requise';
    }

    if (!formData.lastMaintenanceDate) {
      newErrors.lastMaintenanceDate = 'La date de vérification/recharge est requise';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = {
        ...formData,
        planId: formData.planId === '' ? null : formData.planId,
      };
      await apiService.updateEquipment(id, payload);
      toast.success('Équipement mis à jour');
      navigate(returnTo);
    } catch (error) {
      console.error('Failed to update equipment:', error);
      const msg = error.message || "Erreur lors de la mise à jour de l'équipement";
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const clientOptions = clients.map((client) => ({
    value: String(client.id),
    label: `${client.name} - ${client.company}`,
  }));

  const siteOptions = sites.map((site) => ({
    value: String(site.id),
    label: site.name,
  }));

  const extinguisherTypeOptions = Object.values(EXTINGUISHER_TYPES).map((type) => ({
    value: type,
    label: type,
  }));

  const equipmentTypeOptions = [
    { value: EQUIPMENT_TYPES.RIA, label: 'RIA (Robinet Incendie Armé)' },
    { value: EQUIPMENT_TYPES.BDI, label: 'BDI (Bouche Incendie)' },
    { value: EQUIPMENT_TYPES.CABINET, label: 'Armoire Incendie' },
    { value: EQUIPMENT_TYPES.EXTINGUISHER, label: EQUIPMENT_TYPE_LABELS.extinguisher },
  ];

  const showVehicleSection =
    formData.vehicleRegistration ||
    formData.vehicleSite ||
    formData.vehicleType ||
    formData.structure ||
    formData.affectation;

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (errors.load) {
    return (
      <div className="space-y-6">
        <Button variant="outline" icon={ArrowLeft} onClick={() => navigate(returnTo)} className="mb-4">
          Retour
        </Button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{errors.load}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="outline" icon={ArrowLeft} onClick={() => navigate(returnTo)} className="mb-4">
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          Modifier {isExtinguisher ? "l'Extincteur" : "l'Équipement"}
        </h1>
        <p className="text-gray-600 mt-1">Mettez à jour les informations de l'équipement</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            {isExtinguisher ? (
              <Input
                label="Type d'équipement"
                value={EQUIPMENT_TYPE_LABELS.extinguisher}
                disabled
                name="typeDisplay"
                onChange={() => {}}
              />
            ) : (
              <Select
                label="Type d'équipement"
                name="type"
                value={formData.type}
                onChange={handleChange}
                options={equipmentTypeOptions}
                placeholder="Sélectionner le type"
                error={errors.type}
                required
              />
            )}
            <Select
              label="Client"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              options={clientOptions}
              placeholder="Sélectionner un client"
              error={errors.clientId}
              required
            />
            <Select
              label="Site"
              name="siteId"
              value={formData.siteId}
              onChange={handleChange}
              options={siteOptions}
              placeholder={formData.clientId ? 'Sélectionner un site' : "Choisir d'abord un client"}
              error={errors.siteId}
              disabled={!formData.clientId}
              required
            />
            <Input
              label={isExtinguisher ? 'Numéro de série' : 'Numéro de série / Référence'}
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              error={errors.serialNumber}
              required
            />
            {isExtinguisher && (
              <>
                <Input
                  label="N° Inventaire"
                  name="inventoryNumber"
                  value={formData.inventoryNumber}
                  onChange={handleChange}
                  placeholder="Ex: INV-001"
                />
                <Select
                  label="Type d'extincteur"
                  name="extinguisherType"
                  value={formData.extinguisherType}
                  onChange={handleChange}
                  options={extinguisherTypeOptions}
                  placeholder="Sélectionner le type"
                  error={errors.extinguisherType}
                  required
                />
                <Input
                  label="Type détaillé"
                  name="detailedType"
                  value={formData.detailedType}
                  onChange={handleChange}
                  placeholder="Ex: CO2 2 kg"
                />
                <Input
                  label="Capacité (kg ou L)"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                  error={errors.capacity}
                  placeholder="Ex: 6 kg"
                  required
                />
              </>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dates importantes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
            <Input
              label="Date de fabrication"
              type="date"
              name="manufacturingDate"
              value={formData.manufacturingDate}
              onChange={handleChange}
            />
            <Input
              label="Date de vérification / Recharge"
              type="date"
              name="lastMaintenanceDate"
              value={formData.lastMaintenanceDate}
              onChange={handleChange}
              error={errors.lastMaintenanceDate}
              helpText="La prochaine date sera calculée automatiquement (+6 mois)"
              required
            />
            <Input
              label="Prochaine vérification"
              type="date"
              name="nextMaintenanceDate"
              value={formData.nextMaintenanceDate}
              onChange={handleChange}
              helpText="Calculée automatiquement (+6 mois), modifiable si besoin"
            />
            <Input
              label="Date de recharge"
              type="date"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
            />
            <EquipmentCalculatedStatus nextMaintenanceDate={formData.nextMaintenanceDate} />
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Emplacement et état</h2>
          {isExtinguisher ? (
            <div className="space-y-4">
              <Select
                label="Statut (rapports / inventaire)"
                name="status"
                value={formData.status}
                onChange={handleChange}
                options={EQUIPMENT_STORED_STATUS_OPTIONS}
                required
              />
              <Input
                label="Emplacement"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Entrée principale, Bureau 101..."
              />
              <Input
                label="Emplacement détaillé"
                name="detailedLocation"
                value={formData.detailedLocation}
                onChange={handleChange}
                placeholder="Précision pour l'inventaire / plan"
              />
              <Textarea
                label="Aspect physique"
                name="physicalCondition"
                value={formData.physicalCondition}
                onChange={handleChange}
                rows={2}
                placeholder="État général de l'extincteur"
              />
              <Textarea
                label="Pièces détachées"
                name="spareParts"
                value={formData.spareParts}
                onChange={handleChange}
                rows={2}
              />
              <Textarea
                label="Boîte à pharmacie"
                name="firstAidKit"
                value={formData.firstAidKit}
                onChange={handleChange}
                rows={2}
              />
              <Textarea
                label="Observation (inventaire)"
                name="observation"
                value={formData.observation}
                onChange={handleChange}
                rows={2}
                placeholder="Notes, anomalies..."
              />
              <Textarea
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <Select
                label="Statut (rapports / inventaire)"
                name="status"
                value={formData.status}
                onChange={handleChange}
                options={EQUIPMENT_STORED_STATUS_OPTIONS}
                required
              />
              <Input
                label="Emplacement"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Couloir principal, Zone de stockage..."
              />
              <Textarea
                label="Aspect physique / État de l'équipement"
                name="physicalCondition"
                value={formData.physicalCondition}
                onChange={handleChange}
                rows={3}
              />
              <Textarea
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
              />
            </div>
          )}
        </Card>

        {(showVehicleSection || isExtinguisher) && (
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Véhicule / affectation</h2>
            <p className="text-sm text-gray-500 mb-4">
              Renseignez ces champs pour les extincteurs embarqués (parc véhicules).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
              <Input label="Structure" name="structure" value={formData.structure} onChange={handleChange} />
              <Input label="Affectation" name="affectation" value={formData.affectation} onChange={handleChange} />
              <Input label="Type véhicule" name="vehicleType" value={formData.vehicleType} onChange={handleChange} />
              <Input
                label="Immatriculation"
                name="vehicleRegistration"
                value={formData.vehicleRegistration}
                onChange={handleChange}
              />
              <Input label="Site véhicule" name="vehicleSite" value={formData.vehicleSite} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mt-4">
              <Input
                label="Date de récupération"
                type="date"
                name="recoveryDate"
                value={formData.recoveryDate}
                onChange={handleChange}
              />
              <Input
                label="Récupéré par"
                name="recoveredBy"
                value={formData.recoveredBy}
                onChange={handleChange}
                placeholder="Nom du technicien"
              />
            </div>
          </Card>
        )}

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-4">
          <Button type="button" variant="secondary" onClick={() => navigate(returnTo)}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Enregistrer les modifications
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditEquipment;
